/**
 * Team Inbox — Forward-message target picker.
 *
 * A searchable modal over the ALREADY-LOADED conversation list (no extra
 * Firestore round-trip). Picking a row FORWARDS the source message(s) to that
 * chat/project — a file forward reuses the received bytes via storagePath,
 * never a re-upload (dataSource.forwardAttachment); a text forward sends the
 * body (dataSource.sendText). The source chat is excluded (you can't forward
 * to the thread it came from) and email rows are hidden (a forward is a
 * WhatsApp send).
 *
 * Reuses the shipped `.ti-picker` modal shell + `.ti-picker-list` (one scroll
 * region inside the modal, page behind locked — the same Vera-approved pattern
 * ContactPicker ships, no nested scroll-in-scroll).
 */
import { useEffect, useMemo, useState } from 'react';
import type { InboxConversation } from '../lib/types';
import { filterConversationsForForward } from '../lib/inboxStore';

export default function ForwardPicker({
  conversations,
  fileName,
  filesReused = true,
  excludeKey,
  busy = false,
  error = null,
  count = 1,
  progress = null,
  onPick,
  onClose,
}: {
  /** The already-loaded conversation list (the inbox's `rows`). */
  conversations: InboxConversation[];
  /** Label of the single message being forwarded — a file name for media, or
   *  null for a text-only forward (falls back to "this message"). */
  fileName: string | null;
  /** Whether the selection includes at least one FILE (image/document). Only
   *  then is the "original files are reused, nothing re-uploaded" note shown —
   *  a text-only forward has no file to reuse, so the note is dropped (accurate
   *  copy for text-only, media-only, or mixed selections). */
  filesReused?: boolean;
  /** The source conversation key — excluded from the target list. */
  excludeKey: string | null;
  /** A forward send is in flight — disables the rows. */
  busy?: boolean;
  /** Plain-language error from the last forward attempt (role=alert). */
  error?: string | null;
  /** How many messages this forward will send. 1 = the single-message flow;
   *  >1 switches the header/sub-line to the multi-message copy. */
  count?: number;
  /** Live progress while looping the sequential per-message sends (multi only). */
  progress?: { completed: number; total: number } | null;
  /** Forward to the chosen conversation. */
  onPick: (target: InboxConversation) => void;
  onClose: () => void;
}) {
  const multi = count > 1;
  const [q, setQ] = useState('');
  const results = useMemo(
    () => filterConversationsForForward(conversations, q, excludeKey, 40),
    [conversations, q, excludeKey],
  );

  // Escape closes the picker — never mid-send (busy), so a forward in flight
  // is not abandoned. The Thread's own Esc handler stands down while this modal
  // is open (it gates on the multi-picker being closed), so Esc acts on the
  // topmost surface only.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [busy, onClose]);

  return (
    <div
      className="ti-modal-backdrop"
      role="dialog"
      aria-modal
      aria-label={multi ? 'Forward messages to a chat' : 'Forward to a chat'}
      onClick={(e) => {
        // Click on the backdrop (not the panel) closes — unless a send is in
        // flight, so an accidental outside click can't drop an active forward.
        if (e.target === e.currentTarget && !busy) onClose();
      }}
    >
      <div className="ti-picker">
        <div className="ti-compose-head">
          <h3>{multi ? `Forward ${count} messages` : 'Forward message'}</h3>
          <button
            type="button"
            className="ti-compose-x"
            onClick={onClose}
            aria-label="Close"
          >
            ✕
          </button>
        </div>
        <div className="ti-picker-sub">
          {multi ? (
            <>
              Send <b>{count} messages</b> to another chat or project.
              {filesReused
                ? ' The original files are reused, nothing is re-uploaded.'
                : ''}
            </>
          ) : (
            <>
              Send <b>{fileName || 'this message'}</b> to another chat or project.
              {filesReused
                ? ' The original file is reused, nothing is re-uploaded.'
                : ''}
            </>
          )}
        </div>

        {busy && progress && (
          <div className="ti-picker-progress" role="status" aria-live="polite">
            Forwarding {Math.min(progress.completed + 1, progress.total)} of{' '}
            {progress.total}…
          </div>
        )}

        {error && (
          <div className="ti-send-err" role="alert">
            {error}
          </div>
        )}

        <input
          className="ti-picker-search"
          autoFocus
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search chats by name, phone or project…"
          aria-label="Search chats"
        />

        <div className="ti-picker-list" role="listbox">
          {results.length === 0 ? (
            <div className="ti-picker-empty">
              {q.trim() ? 'No chat matches that search.' : 'No chats to forward to.'}
            </div>
          ) : (
            results.map((c) => (
              <button
                key={c.key}
                type="button"
                role="option"
                className="ti-picker-row"
                disabled={busy}
                onClick={() => onPick(c)}
              >
                <span className="ti-picker-row-main">
                  <span className="ti-picker-row-name">
                    {c.name || c.phone || '(unknown)'}
                    {c.projectRef && (
                      <span className="ti-picker-row-cno">{c.projectRef}</span>
                    )}
                  </span>
                  <span className="ti-picker-row-sub">
                    {c.phone || ''}
                    {c.phone && c.number?.label ? ' · ' : ''}
                    {c.number?.label || ''}
                  </span>
                </span>
              </button>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
